import React, { useState, useEffect, useCallback } from 'react';
import apiService from '../../services/api';
import type { InstagramPost, InstagramCampaign, AnalyticsOverview } from './types';

interface ThemePerformance {
  theme: string;
  posts: number;
  likes: number;
  comments: number;
  reach: number;
}

interface CampaignTotals {
  impressions: number;
  reach: number;
  clicks: number;
  conversions: number;
  spent: number;
  ctr: number;
  cpc: number;
  costPerConversion: number;
}

const AnalyticsComponent: React.FC = () => {
  const [overview, setOverview] = useState<AnalyticsOverview | null>(null);
  const [posts, setPosts] = useState<InstagramPost[]>([]);
  const [themePerformance, setThemePerformance] = useState<ThemePerformance[]>([]);
  const [campaigns, setCampaigns] = useState<InstagramCampaign[]>([]);
  const [campaignTotals, setCampaignTotals] = useState<CampaignTotals | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const [overviewRes, postsRes, campaignsRes] = await Promise.all([
        apiService.getInstagramAnalyticsOverview(),
        apiService.getInstagramPostAnalytics(),
        apiService.getInstagramCampaignAnalytics(),
      ]);

      setOverview((overviewRes as unknown as { overview: AnalyticsOverview }).overview);

      const postData = postsRes as unknown as {
        posts: InstagramPost[]; themePerformance: ThemePerformance[];
      };
      setPosts(postData.posts || []);
      setThemePerformance(postData.themePerformance || []);

      const campaignData = campaignsRes as unknown as {
        campaigns: InstagramCampaign[]; totals: CampaignTotals;
      };
      setCampaigns(campaignData.campaigns || []);
      setCampaignTotals(campaignData.totals);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load analytics');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-blue" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-2xl p-6 border border-brand-red/30 shadow-sm">
        <p className="text-brand-red font-body">{error}</p>
        <button
          onClick={loadData}
          className="mt-4 bg-brand-blue hover:bg-brand-blue-dark text-white font-bold px-6 py-3 rounded-xl transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  const topPosts = [...posts]
    .sort((a, b) => (b.engagement.likes + b.engagement.comments) - (a.engagement.likes + a.engagement.comments))
    .slice(0, 5);

  return (
    <div className="space-y-6">
      {/* Account */}
      <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
        <h3 className="text-xl font-title font-bold text-deep-navy mb-4">Account</h3>

        {overview?.account ? (
          <div className="grid md:grid-cols-4 gap-4">
            <div className="bg-frost-white rounded-xl p-4">
              <p className="text-sm text-gray-600 font-body">Followers</p>
              <p className="text-2xl font-bold text-brand-blue">
                {overview.account.followers_count?.toLocaleString() ?? '—'}
              </p>
            </div>
            <div className="bg-frost-white rounded-xl p-4">
              <p className="text-sm text-gray-600 font-body">Following</p>
              <p className="text-2xl font-bold text-deep-navy">
                {overview.account.follows_count?.toLocaleString() ?? '—'}
              </p>
            </div>
            <div className="bg-frost-white rounded-xl p-4">
              <p className="text-sm text-gray-600 font-body">Posts</p>
              <p className="text-2xl font-bold text-deep-navy">
                {overview.account.media_count?.toLocaleString() ?? '—'}
              </p>
            </div>
            <div className="bg-frost-white rounded-xl p-4">
              <p className="text-sm text-gray-600 font-body">Engagement Rate</p>
              <p className="text-2xl font-bold text-scarf-green">{overview.engagementRate}%</p>
            </div>
          </div>
        ) : (
          <p className="text-gray-600 font-body py-4">
            Account metrics appear once an Instagram account is connected in Settings.
          </p>
        )}
      </div>

      {/* This month */}
      <div className="grid md:grid-cols-4 gap-6">
        <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
          <h3 className="text-lg font-title font-bold text-deep-navy mb-2">Likes</h3>
          <div className="text-3xl font-bold text-brand-blue">{overview?.totalLikes ?? 0}</div>
          <p className="text-gray-600 text-sm">This month</p>
        </div>
        <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
          <h3 className="text-lg font-title font-bold text-deep-navy mb-2">Comments</h3>
          <div className="text-3xl font-bold text-brand-blue-light">{overview?.totalComments ?? 0}</div>
          <p className="text-gray-600 text-sm">This month</p>
        </div>
        <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
          <h3 className="text-lg font-title font-bold text-deep-navy mb-2">Reach</h3>
          <div className="text-3xl font-bold text-deep-navy">{overview?.totalReach ?? 0}</div>
          <p className="text-gray-600 text-sm">Unique accounts</p>
        </div>
        <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
          <h3 className="text-lg font-title font-bold text-deep-navy mb-2">Impressions</h3>
          <div className="text-3xl font-bold text-deep-navy">{overview?.totalImpressions ?? 0}</div>
          <p className="text-gray-600 text-sm">Total views</p>
        </div>
      </div>

      {/* Theme performance */}
      <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
        <h3 className="text-xl font-title font-bold text-deep-navy mb-4">What Works Best</h3>

        {themePerformance.length === 0 ? (
          <p className="text-gray-600 font-body py-8 text-center">
            Theme performance appears once posts have been published and their metrics synced.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-ice-teal-light">
                  <th className="text-left py-3 px-4 text-deep-navy font-bold">Theme</th>
                  <th className="text-left py-3 px-4 text-deep-navy font-bold">Posts</th>
                  <th className="text-left py-3 px-4 text-deep-navy font-bold">Likes</th>
                  <th className="text-left py-3 px-4 text-deep-navy font-bold">Comments</th>
                  <th className="text-left py-3 px-4 text-deep-navy font-bold">Avg Likes/Post</th>
                </tr>
              </thead>
              <tbody>
                {themePerformance.map((t) => (
                  <tr key={t.theme} className="border-b border-gray-100 hover:bg-frost-white transition-colors">
                    <td className="py-3 px-4 font-body text-deep-navy font-bold">{t.theme}</td>
                    <td className="py-3 px-4 font-body text-gray-600">{t.posts}</td>
                    <td className="py-3 px-4 font-body text-gray-600">{t.likes}</td>
                    <td className="py-3 px-4 font-body text-gray-600">{t.comments}</td>
                    <td className="py-3 px-4 font-body text-brand-blue font-bold">
                      {t.posts ? Math.round(t.likes / t.posts) : 0}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Top posts */}
      <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
        <h3 className="text-xl font-title font-bold text-deep-navy mb-4">Top Posts</h3>

        {topPosts.length === 0 ? (
          <p className="text-gray-600 font-body py-8 text-center">No published posts yet.</p>
        ) : (
          <div className="space-y-3">
            {topPosts.map((post) => (
              <div key={post._id} className="bg-frost-white rounded-xl p-4 flex gap-4 items-center">
                {post.mediaUrls[0] && (
                  <img src={post.mediaUrls[0]} alt="" className="w-16 h-16 rounded-lg object-cover flex-shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <p className="font-body text-deep-navy truncate">{post.caption}</p>
                  <p className="text-sm text-gray-600 font-body">
                    {post.engagement.likes} likes · {post.engagement.comments} comments · {post.engagement.reach} reach
                  </p>
                </div>
                {post.igPermalink && (
                  <a
                    href={post.igPermalink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-brand-blue hover:text-brand-blue-dark font-bold text-sm flex-shrink-0"
                  >
                    View
                  </a>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Campaign performance */}
      <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
        <h3 className="text-xl font-title font-bold text-deep-navy mb-4">Paid Performance</h3>

        {campaigns.length === 0 ? (
          <p className="text-gray-600 font-body py-8 text-center">No campaign data yet.</p>
        ) : (
          <>
            <div className="grid md:grid-cols-4 gap-4 mb-6">
              <div className="bg-frost-white rounded-xl p-4">
                <p className="text-sm text-gray-600 font-body">Total Spend</p>
                <p className="text-2xl font-bold text-brand-red">${campaignTotals?.spent.toFixed(2)}</p>
              </div>
              <div className="bg-frost-white rounded-xl p-4">
                <p className="text-sm text-gray-600 font-body">Clicks</p>
                <p className="text-2xl font-bold text-brand-blue">{campaignTotals?.clicks}</p>
              </div>
              <div className="bg-frost-white rounded-xl p-4">
                <p className="text-sm text-gray-600 font-body">CTR</p>
                <p className="text-2xl font-bold text-deep-navy">{campaignTotals?.ctr}%</p>
              </div>
              <div className="bg-frost-white rounded-xl p-4">
                <p className="text-sm text-gray-600 font-body">Cost / Conversion</p>
                <p className="text-2xl font-bold text-scarf-green">
                  ${campaignTotals?.costPerConversion.toFixed(2)}
                </p>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-ice-teal-light">
                    <th className="text-left py-3 px-4 text-deep-navy font-bold">Campaign</th>
                    <th className="text-left py-3 px-4 text-deep-navy font-bold">Spent</th>
                    <th className="text-left py-3 px-4 text-deep-navy font-bold">Impressions</th>
                    <th className="text-left py-3 px-4 text-deep-navy font-bold">Clicks</th>
                    <th className="text-left py-3 px-4 text-deep-navy font-bold">CTR</th>
                    <th className="text-left py-3 px-4 text-deep-navy font-bold">Conversions</th>
                  </tr>
                </thead>
                <tbody>
                  {campaigns.map((c) => (
                    <tr key={c._id} className="border-b border-gray-100 hover:bg-frost-white transition-colors">
                      <td className="py-3 px-4 font-body text-deep-navy font-bold">{c.name}</td>
                      <td className="py-3 px-4 font-body text-gray-600">${c.spent.toFixed(2)}</td>
                      <td className="py-3 px-4 font-body text-gray-600">{c.performance.impressions}</td>
                      <td className="py-3 px-4 font-body text-gray-600">{c.performance.clicks}</td>
                      <td className="py-3 px-4 font-body text-gray-600">{c.performance.ctr.toFixed(2)}%</td>
                      <td className="py-3 px-4 font-body text-scarf-green font-bold">
                        {c.performance.conversions}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AnalyticsComponent;
